import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../stores';
import './Admin.css';

interface PaymentProvider {
  id: number;
  name: string;
  displayName?: string;
  providerType: string;
  apiKey: string;
  webhookSecret: string;
  isActive: boolean;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PaymentSubscription {
  id: number;
  providerId: number;
  name: string;
  externalId?: string;
  priceAmount: number;
  currency: string;
  interval: string;
  description?: string;
  credits?: number;
  stripePriceId?: string;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface PaymentProviderFormData {
  name: string;
  displayName: string;
  providerType: string;
  customProviderType: string;
  apiKey: string;
  webhookSecret: string;
  isActive: boolean;
  description: string;
}

interface PaymentSubscriptionFormData {
  name: string;
  externalId: string;
  priceAmount: string;
  currency: string;
  interval: string;
  description: string;
  credits: string;
  stripePriceId: string;
  isActive: boolean;
}

export function PaymentProviders() {
  const { accessToken } = useAuthStore();
  const [providers, setProviders] = useState<PaymentProvider[]>([]);
  const [subscriptionsByProvider, setSubscriptionsByProvider] = useState<Record<number, PaymentSubscription[]>>({});
  const [expandedProviderId, setExpandedProviderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<number | string | null>(null);
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<PaymentProvider | null>(null);
  const [editingSubscription, setEditingSubscription] = useState<PaymentSubscription | null>(null);
  const [subscriptionProviderId, setSubscriptionProviderId] = useState<number | null>(null);
  const [providerFormData, setProviderFormData] = useState<PaymentProviderFormData>({
    name: '',
    displayName: '',
    providerType: 'stripe',
    customProviderType: '',
    apiKey: '',
    webhookSecret: '',
    isActive: true,
    description: '',
  });
  const [subscriptionFormData, setSubscriptionFormData] = useState<PaymentSubscriptionFormData>({
    name: '',
    externalId: '',
    priceAmount: '',
    currency: 'TRY',
    interval: 'monthly',
    description: '',
    credits: '',
    stripePriceId: '',
    isActive: true,
  });

  const providerTypes = [
    { value: 'stripe', label: 'Stripe' },
    { value: 'iyzico', label: 'iyzico' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'shopier', label: 'Shopier' },
    { value: 'manual', label: 'Manual' },
    { value: 'custom', label: 'Custom' },
  ];

  const subscriptionIntervals = [
    { value: 'monthly', label: 'Aylık' },
    { value: 'yearly', label: 'Yıllık' },
    { value: 'one_time', label: 'Tek seferlik' },
  ];

  const fetchProviders = useCallback(async () => {
    try {
      const response = await fetch('/api/admin/payment-providers', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch payment providers');
      const data = await response.json();
      setProviders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load payment providers');
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  const fetchSubscriptions = useCallback(
    async (providerId: number) => {
      try {
        const response = await fetch(`/api/admin/payment-providers/${providerId}/subscriptions`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!response.ok) return;
        const data = await response.json();
        setSubscriptionsByProvider((prev) => ({ ...prev, [providerId]: data }));
      } catch {
        setSubscriptionsByProvider((prev) => ({ ...prev, [providerId]: [] }));
      }
    },
    [accessToken]
  );

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  const handleExpandProvider = (providerId: number) => {
    setExpandedProviderId((prev) => (prev === providerId ? null : providerId));
    if (!subscriptionsByProvider[providerId]) {
      fetchSubscriptions(providerId);
    }
  };

  const maskSecret = (secret: string): string => {
    if (!secret || secret.length < 8) return '••••••••';
    return `${secret.substring(0, 4)}${'•'.repeat(secret.length - 8)}${secret.substring(secret.length - 4)}`;
  };

  const handleOpenProviderModal = (provider?: PaymentProvider) => {
    if (provider) {
      setEditingProvider(provider);
      const isCustom = !providerTypes.some((p) => p.value === provider.providerType);
      setProviderFormData({
        name: provider.name,
        displayName: provider.displayName || '',
        providerType: isCustom ? 'custom' : provider.providerType,
        customProviderType: isCustom ? provider.providerType : '',
        apiKey: '',
        webhookSecret: '',
        isActive: provider.isActive,
        description: provider.description || '',
      });
    } else {
      setEditingProvider(null);
      setProviderFormData({
        name: '',
        displayName: '',
        providerType: 'stripe',
        customProviderType: '',
        apiKey: '',
        webhookSecret: '',
        isActive: true,
        description: '',
      });
    }
    setShowProviderModal(true);
    setError('');
  };

  const handleCloseProviderModal = () => {
    setShowProviderModal(false);
    setEditingProvider(null);
    setError('');
  };

  const handleOpenSubscriptionModal = (providerId: number, subscription?: PaymentSubscription) => {
    setSubscriptionProviderId(providerId);
    if (subscription) {
      setEditingSubscription(subscription);
      setSubscriptionFormData({
        name: subscription.name,
        externalId: subscription.externalId || '',
        priceAmount: String(subscription.priceAmount),
        currency: subscription.currency || 'TRY',
        interval: subscription.interval || 'monthly',
        description: subscription.description || '',
        credits: subscription.credits != null ? String(subscription.credits) : '',
        stripePriceId: subscription.stripePriceId || '',
        isActive: subscription.isActive,
      });
    } else {
      setEditingSubscription(null);
      setSubscriptionFormData({
        name: '',
        externalId: '',
        priceAmount: '',
        currency: 'TRY',
        interval: 'monthly',
        description: '',
        credits: '',
        stripePriceId: '',
        isActive: true,
      });
    }
    setShowSubscriptionModal(true);
    setError('');
  };

  const handleCloseSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setEditingSubscription(null);
    setSubscriptionProviderId(null);
    setError('');
  };

  const handleProviderSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setActionLoading(editingProvider?.id ?? 0);
    const providerTypeToSend =
      providerFormData.providerType === 'custom'
        ? (providerFormData.customProviderType.trim() || 'custom')
        : providerFormData.providerType;
    if (providerFormData.providerType === 'custom' && !providerFormData.customProviderType.trim()) {
      setError('Custom tip seçildiğinde tip adı girin');
      setActionLoading(null);
      return;
    }
    try {
      const url = editingProvider ? `/api/admin/payment-providers/${editingProvider.id}` : '/api/admin/payment-providers';
      const method = editingProvider ? 'PUT' : 'POST';
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ ...providerFormData, providerType: providerTypeToSend }),
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save payment provider');
      }
      await fetchProviders();
      handleCloseProviderModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save payment provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSubscriptionSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (subscriptionProviderId == null) return;
    setError('');
    setActionLoading(editingSubscription ? `sub-${editingSubscription.id}` : `sub-new`);
    try {
      const priceVal = parseFloat(subscriptionFormData.priceAmount);
      if (isNaN(priceVal) || priceVal < 0) {
        setError('Geçerli bir fiyat girin');
        setActionLoading(null);
        return;
      }
      const creditsVal = parseInt(subscriptionFormData.credits, 10);
      const body = {
        name: subscriptionFormData.name,
        externalId: subscriptionFormData.externalId || null,
        priceAmount: priceVal,
        currency: subscriptionFormData.currency,
        interval: subscriptionFormData.interval,
        description: subscriptionFormData.description || null,
        credits: Number.isNaN(creditsVal) ? 0 : creditsVal,
        stripePriceId: subscriptionFormData.stripePriceId || null,
        isActive: subscriptionFormData.isActive,
      };
      if (editingSubscription) {
        const response = await fetch(`/api/admin/payment-subscriptions/${editingSubscription.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to update subscription');
        }
      } else {
        const response = await fetch(`/api/admin/payment-providers/${subscriptionProviderId}/subscriptions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.message || 'Failed to create subscription');
        }
      }
      await fetchSubscriptions(subscriptionProviderId);
      handleCloseSubscriptionModal();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleProvider = async (provider: PaymentProvider) => {
    setActionLoading(provider.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/payment-providers/${provider.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to toggle provider');
      }
      await fetchProviders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleSubscription = async (sub: PaymentSubscription) => {
    setActionLoading(`sub-${sub.id}`);
    setError('');
    try {
      const response = await fetch(`/api/admin/payment-subscriptions/${sub.id}/toggle`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to toggle subscription');
      }
      await fetchSubscriptions(sub.providerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteProvider = async (provider: PaymentProvider) => {
    if (!confirm(`"${provider.name}" ödeme aracını silmek istediğinize emin misiniz? Tüm subscription'lar da silinecek.`)) return;
    setActionLoading(provider.id);
    setError('');
    try {
      const response = await fetch(`/api/admin/payment-providers/${provider.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete provider');
      }
      await fetchProviders();
      setSubscriptionsByProvider((prev) => {
        const next = { ...prev };
        delete next[provider.id];
        return next;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete provider');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteSubscription = async (sub: PaymentSubscription) => {
    if (!confirm(`"${sub.name}" subscription'ı silmek istediğinize emin misiniz?`)) return;
    setActionLoading(`sub-${sub.id}`);
    setError('');
    try {
      const response = await fetch(`/api/admin/payment-subscriptions/${sub.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete subscription');
      }
      await fetchSubscriptions(sub.providerId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete subscription');
    } finally {
      setActionLoading(null);
    }
  };

  const getProviderTypeLabel = (type: string) => providerTypes.find((p) => p.value === type)?.label || type;
  const getIntervalLabel = (interval: string) => subscriptionIntervals.find((i) => i.value === interval)?.label || interval;

  if (loading) return <div className="admin-loading">Yükleniyor...</div>;

  return (
    <div className="admin-page">
      <header className="admin-page-header">
        <div>
          <h1>Payment Providers</h1>
          <p>Ödeme araçlarını ve subscription planlarını yönetin</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={() => handleOpenProviderModal()}>
          + Yeni Ödeme Aracı Ekle
        </button>
      </header>

      {error && <div className="admin-error">{error}</div>}

      <section className="admin-table-section">
        <div className="admin-table-toolbar">
          <h2>Payment Providers ({providers.length})</h2>
        </div>

        {providers.length === 0 ? (
          <div className="admin-empty-state">
            <p>Henüz ödeme aracı tanımlanmamış.</p>
            <button className="admin-btn admin-btn-primary" onClick={() => handleOpenProviderModal()}>
              İlk Ödeme Aracını Ekle
            </button>
          </div>
        ) : (
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th>Ad</th>
                  <th>Tip</th>
                  <th>API Key</th>
                  <th>Webhook Secret</th>
                  <th>Durum</th>
                  <th>Açıklama</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {providers.map((provider) => (
                  <React.Fragment key={provider.id}>
                    <tr
                      key={provider.id}
                      className={expandedProviderId === provider.id ? 'admin-table-row-expanded' : ''}
                      onClick={() => handleExpandProvider(provider.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>
                        <span className="admin-expand-icon">{expandedProviderId === provider.id ? '▼' : '▶'}</span>
                      </td>
                      <td>
                        <div className="provider-info">
                          <span className="provider-name">{provider.displayName || provider.name}</span>
                          <span className="provider-description">{provider.name}</span>
                        </div>
                      </td>
                      <td>
                        <span className="provider-type-badge">{getProviderTypeLabel(provider.providerType)}</span>
                      </td>
                      <td>
                        <span className="provider-api-key">{maskSecret(provider.apiKey)}</span>
                      </td>
                      <td>
                        <span className="provider-api-key">{maskSecret(provider.webhookSecret)}</span>
                      </td>
                      <td>
                        <button
                          type="button"
                          className={`admin-toggle-btn ${provider.isActive ? 'active' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleProvider(provider);
                          }}
                          disabled={actionLoading === provider.id}
                          title={provider.isActive ? 'Deaktif Et' : 'Aktif Et'}
                        >
                          {provider.isActive ? '✓ Aktif' : '✗ Pasif'}
                        </button>
                      </td>
                      <td>{provider.description || '-'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="admin-action-buttons">
                          <button
                            className="admin-edit-btn"
                            onClick={() => handleOpenProviderModal(provider)}
                            disabled={actionLoading === provider.id}
                            title="Düzenle"
                          >
                            ✏️
                          </button>
                          <button
                            className="admin-delete-btn"
                            onClick={() => handleDeleteProvider(provider)}
                            disabled={actionLoading === provider.id}
                            title="Sil"
                          >
                            {actionLoading === provider.id ? '...' : '🗑'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedProviderId === provider.id && (
                      <tr key={`${provider.id}-subs`} className="admin-table-sub-row">
                        <td colSpan={8}>
                          <div className="admin-subscriptions-panel">
                            <div className="admin-subscriptions-header">
                              <h3>Subscription Planları</h3>
                              <button
                                type="button"
                                className="admin-btn admin-btn-primary admin-btn-xs"
                                onClick={() => handleOpenSubscriptionModal(provider.id)}
                              >
                                + Subscription Ekle
                              </button>
                            </div>
                            {subscriptionsByProvider[provider.id] ? (
                              subscriptionsByProvider[provider.id].length === 0 ? (
                                <div className="admin-subscriptions-empty">
                                  <p>Bu provider için henüz subscription yok.</p>
                                  <button
                                    type="button"
                                    className="admin-btn admin-btn-primary"
                                    onClick={() => handleOpenSubscriptionModal(provider.id)}
                                  >
                                    İlk Subscription'ı Ekle
                                  </button>
                                </div>
                              ) : (
                                <table className="admin-table admin-table-nested">
                                  <thead>
                                    <tr>
                                      <th>Plan Adı</th>
                                      <th>External ID</th>
                                      <th>Kredi</th>
                                      <th>Fiyat</th>
                                      <th>Periyot</th>
                                      <th>Durum</th>
                                      <th>İşlemler</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {subscriptionsByProvider[provider.id].map((sub) => (
                                      <tr key={sub.id}>
                                        <td>{sub.name}</td>
                                        <td>
                                          <code className="admin-code-inline">{sub.externalId || '-'}</code>
                                        </td>
                                        <td>{sub.credits ?? '-'}</td>
                                        <td>
                                          {sub.priceAmount} {sub.currency}
                                        </td>
                                        <td>{getIntervalLabel(sub.interval)}</td>
                                        <td>
                                          <button
                                            type="button"
                                            className={`admin-toggle-btn ${sub.isActive ? 'active' : ''}`}
                                            onClick={() => handleToggleSubscription(sub)}
                                            disabled={actionLoading === `sub-${sub.id}`}
                                          >
                                            {sub.isActive ? '✓ Aktif' : '✗ Pasif'}
                                          </button>
                                        </td>
                                        <td>
                                          <div className="admin-action-buttons">
                                            <button
                                              type="button"
                                              className="admin-edit-btn"
                                              onClick={() => handleOpenSubscriptionModal(provider.id, sub)}
                                              disabled={actionLoading === `sub-${sub.id}`}
                                              title="Düzenle"
                                            >
                                              ✏️
                                            </button>
                                            <button
                                              type="button"
                                              className="admin-delete-btn"
                                              onClick={() => handleDeleteSubscription(sub)}
                                              disabled={actionLoading === `sub-${sub.id}`}
                                              title="Sil"
                                            >
                                              {actionLoading === `sub-${sub.id}` ? '...' : '🗑'}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )
                            ) : (
                              <div className="admin-subscriptions-loading">Yükleniyor...</div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Provider Modal */}
      {showProviderModal && (
        <div className="admin-modal-overlay" onClick={handleCloseProviderModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingProvider ? 'Ödeme Aracı Düzenle' : 'Yeni Ödeme Aracı Ekle'}</h2>
              <button className="admin-modal-close" onClick={handleCloseProviderModal}>✕</button>
            </div>
            <form onSubmit={handleProviderSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Teknik Ad *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={providerFormData.name}
                    onChange={(e) => setProviderFormData({ ...providerFormData, name: e.target.value })}
                    required
                    placeholder="Orn: stripe-production"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Görünen Ad
                  <input
                    type="text"
                    className="admin-form-input"
                    value={providerFormData.displayName}
                    onChange={(e) => setProviderFormData({ ...providerFormData, displayName: e.target.value })}
                    placeholder="Orn: Stripe Production"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Tip *
                  <select
                    className="admin-form-select"
                    value={providerFormData.providerType}
                    onChange={(e) => setProviderFormData({ ...providerFormData, providerType: e.target.value })}
                    required
                  >
                    {providerTypes.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              {providerFormData.providerType === 'custom' && (
                <div className="admin-form-group">
                  <label className="admin-form-label">
                    Custom Tip Adı *
                    <input
                      type="text"
                      className="admin-form-input"
                      value={providerFormData.customProviderType}
                      onChange={(e) => setProviderFormData({ ...providerFormData, customProviderType: e.target.value })}
                      placeholder="Örn: papara, paytr, param"
                    />
                  </label>
                </div>
              )}
              <div className="admin-form-group">
                <label className="admin-form-label">
                  API Key *
                  <input
                    type="password"
                    className="admin-form-input"
                    value={providerFormData.apiKey}
                    onChange={(e) => setProviderFormData({ ...providerFormData, apiKey: e.target.value })}
                    required={!editingProvider}
                    placeholder={editingProvider ? 'Değiştirmek için yeni key girin' : 'pk_test_...'}
                  />
                  {editingProvider && <small className="admin-form-hint">Değiştirmek istemiyorsanız boş bırakın</small>}
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Webhook Secret
                  <input
                    type="password"
                    className="admin-form-input"
                    value={providerFormData.webhookSecret}
                    onChange={(e) => setProviderFormData({ ...providerFormData, webhookSecret: e.target.value })}
                    placeholder={editingProvider ? 'Değiştirmek için yeni secret girin' : 'whsec_...'}
                  />
                  {editingProvider && <small className="admin-form-hint">Değiştirmek istemiyorsanız boş bırakın</small>}
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Açıklama
                  <textarea
                    className="admin-form-textarea"
                    value={providerFormData.description}
                    onChange={(e) => setProviderFormData({ ...providerFormData, description: e.target.value })}
                    placeholder="Ödeme aracına dair notlar..."
                    rows={3}
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={providerFormData.isActive}
                    onChange={(e) => setProviderFormData({ ...providerFormData, isActive: e.target.checked })}
                  />
                  <span>Aktif</span>
                </label>
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={handleCloseProviderModal} disabled={actionLoading !== null}>
                  İptal
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={actionLoading !== null}>
                  {actionLoading !== null ? 'Kaydediliyor...' : editingProvider ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subscription Modal */}
      {showSubscriptionModal && subscriptionProviderId != null && (
        <div className="admin-modal-overlay" onClick={handleCloseSubscriptionModal}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>{editingSubscription ? 'Subscription Düzenle' : 'Yeni Subscription Ekle'}</h2>
              <button className="admin-modal-close" onClick={handleCloseSubscriptionModal}>✕</button>
            </div>
            <form onSubmit={handleSubscriptionSubmit} className="admin-form">
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Plan Adı *
                  <input
                    type="text"
                    className="admin-form-input"
                    value={subscriptionFormData.name}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, name: e.target.value })}
                    required
                    placeholder="Örn: Pro Plan"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  External ID (Stripe buy button id, price_id vb.)
                  <input
                    type="text"
                    className="admin-form-input"
                    value={subscriptionFormData.externalId}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, externalId: e.target.value })}
                    placeholder="Örn: buy_btn_xxx veya price_xxx"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Kredi Miktarı
                  <input
                    type="number"
                    min="1"
                    className="admin-form-input"
                    value={subscriptionFormData.credits}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, credits: e.target.value })}
                    placeholder="Örn: 1200"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Stripe Price ID (webhook eşleştirme, opsiyonel)
                  <input
                    type="text"
                    className="admin-form-input"
                    value={subscriptionFormData.stripePriceId}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, stripePriceId: e.target.value })}
                    placeholder="Örn: price_xxx"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Fiyat *
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="admin-form-input"
                    value={subscriptionFormData.priceAmount}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, priceAmount: e.target.value })}
                    placeholder="9.99"
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Para Birimi
                  <select
                    className="admin-form-select"
                    value={subscriptionFormData.currency}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, currency: e.target.value })}
                  >
                    <option value="TRY">TRY</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="USDT">USDT (Tether)</option>
                  </select>
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Periyot
                  <select
                    className="admin-form-select"
                    value={subscriptionFormData.interval}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, interval: e.target.value })}
                  >
                    {subscriptionIntervals.map((i) => (
                      <option key={i.value} value={i.value}>{i.label}</option>
                    ))}
                  </select>
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">
                  Açıklama
                  <textarea
                    className="admin-form-textarea"
                    value={subscriptionFormData.description}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, description: e.target.value })}
                    placeholder="Plan açıklaması..."
                    rows={2}
                  />
                </label>
              </div>
              <div className="admin-form-group">
                <label className="admin-setting-checkbox">
                  <input
                    type="checkbox"
                    checked={subscriptionFormData.isActive}
                    onChange={(e) => setSubscriptionFormData({ ...subscriptionFormData, isActive: e.target.checked })}
                  />
                  <span>Aktif</span>
                </label>
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={handleCloseSubscriptionModal} disabled={actionLoading !== null}>
                  İptal
                </button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={actionLoading !== null}>
                  {actionLoading !== null ? 'Kaydediliyor...' : editingSubscription ? 'Güncelle' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
